import React, { ReactNode, useContext, useMemo, useState } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { Button, Flex, Table, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import dayjs from 'dayjs';

import {
  ALL_SENSORS,
  AssetCountRow,
  AssetCountTotals,
} from '@totem/components/administration/reconciliation/nozomi/vantage/types';
import {
  buildRows,
  getTotals,
  isCountFailed,
} from '@totem/components/administration/reconciliation/nozomi/vantage/utilities';
import VantageAssetCountsContext from '@totem/components/administration/reconciliation/nozomi/vantage/VantageAssetCountsContext';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { formatElapsedSeconds } from '@totem/utilities/timeUtilities';

const NEVER = '0001';

const formatCount = (value: number) =>
  value === 0 ? (
    <Typography.Text type="secondary">–</Typography.Text>
  ) : (
    value.toLocaleString()
  );

type CountColumn = {
  title: string;
  key: keyof AssetCountTotals;
  tooltip: string;
};

const countColumn = ({ title, key, tooltip }: CountColumn) => ({
  title: <Tooltip title={tooltip}>{title}</Tooltip>,
  key,
  align: 'right' as const,
  render: (_: unknown, record: AssetCountRow) => formatCount(record[key]),
});

const ACTIVITY_COLUMNS: CountColumn[] = [
  {
    title: 'Total',
    key: 'total',
    tooltip:
      'Every asset Vantage holds for the sensor. This should match the Assets total in Vantage with Last activity time set to All.',
  },
  {
    title: 'Active 24h',
    key: 'active24h',
    tooltip: 'Assets with activity in the 24 hours before the count.',
  },
  {
    title: 'Active 7d',
    key: 'active7d',
    tooltip: 'Assets with activity in the 7 days before the count.',
  },
  {
    title: 'Active 30d',
    key: 'active30d',
    tooltip: 'Assets with activity in the 30 days before the count.',
  },
];

const MAC_COLUMNS: CountColumn[] = [
  {
    title: 'No MAC',
    key: 'noMac',
    tooltip: 'Assets with no MAC address at all.',
  },
  {
    title: 'Local MAC Only',
    key: 'localMacOnly',
    tooltip:
      'Assets whose only MACs are locally administered or randomized. The portal sync skips these.',
  },
];

const CountedCell = ({ record }: { record: AssetCountRow }) => {
  const { count } = record;
  if (!count) {
    return <Tag>Not counted</Tag>;
  }

  const counted = !!count.countedAt && !count.countedAt.startsWith(NEVER);
  return (
    <Flex vertical align="flex-start" gap={2}>
      {counted && (
        <Typography.Text style={{ whiteSpace: 'nowrap' }}>
          {dayjs(count.countedAt).format('MMM D, h:mm A')}
        </Typography.Text>
      )}
      {isCountFailed(record) ? (
        <Tooltip
          title={`${dayjs(count.lastAttemptAt).format('MMM D, h:mm A')}: ${count.error}`}
        >
          <Tag color="red">Last count failed</Tag>
        </Tooltip>
      ) : (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {formatElapsedSeconds(count.durationSeconds)}
        </Typography.Text>
      )}
    </Flex>
  );
};

const SensorRefresh = ({ record }: { record: AssetCountRow }) => {
  const { refreshing, onRefreshCounts } = useContext(VantageAssetCountsContext);
  const busy = !!refreshing[record.siteId] || !!refreshing[ALL_SENSORS];

  return (
    <Tooltip title="Recount from Vantage">
      <Button
        size="small"
        icon={<ReloadOutlined />}
        loading={busy}
        disabled={busy}
        aria-label={`Recount ${record.name}`}
        onClick={() => onRefreshCounts(record.siteId)}
      />
    </Tooltip>
  );
};

type Props = {
  expandedKeys: string[];
  onExpandedKeysChange: (keys: string[]) => void;
};

const VantageAssetCountsTable = ({
  expandedKeys,
  onExpandedKeysChange,
}: Props) => {
  const { data, loading } = useContext(VantageAssetCountsContext);
  const [pageSize, setPageSize] = useState<number>(25);

  const sensors = useMemo(() => data?.sensors ?? [], [data]);
  const rows = useMemo(() => buildRows(sensors), [sensors]);
  const totals = useMemo(() => getTotals(sensors), [sensors]);

  const categoryColumns: CountColumn[] = useMemo(
    () => [
      { title: 'IT', key: 'it', tooltip: 'Vantage technology category IT.' },
      { title: 'OT', key: 'ot', tooltip: 'Vantage technology category OT.' },
      {
        title: 'IoT',
        key: 'iot',
        tooltip: 'Vantage technology category IoT.',
      },
      ...(totals.otherCategory > 0
        ? [
            {
              title: 'Other',
              key: 'otherCategory' as const,
              tooltip:
                'Assets with a blank or unrecognized technology category.',
            },
          ]
        : []),
    ],
    [totals.otherCategory],
  );

  const columns: ColumnsType<AssetCountRow> = [
    {
      title: 'Organization / Sensor',
      key: 'name',
      width: 300,
      render: (_, record) =>
        record.kind === 'organization' ? (
          <Flex gap={8} align="center" wrap>
            <Typography.Text strong>{record.name}</Typography.Text>
            <Tag>
              {record.sensorCount}{' '}
              {record.sensorCount === 1 ? 'sensor' : 'sensors'}
            </Tag>
          </Flex>
        ) : (
          <Flex vertical gap={2}>
            <Typography.Text code style={{ whiteSpace: 'nowrap' }}>
              {record.name}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {record.building}
            </Typography.Text>
            {!record.mapped && (
              <Tooltip title="No building is assigned, so the portal sync skips this sensor. Vantage still reports its assets.">
                <Tag style={{ marginInlineEnd: 0, alignSelf: 'flex-start' }}>
                  Not mapped
                </Tag>
              </Tooltip>
            )}
          </Flex>
        ),
    },
    { title: 'Vantage Assets', children: ACTIVITY_COLUMNS.map(countColumn) },
    {
      title: 'Technology Category',
      children: categoryColumns.map(countColumn),
    },
    { title: 'MAC Addresses', children: MAC_COLUMNS.map(countColumn) },
    {
      title: 'Counted',
      key: 'counted',
      render: (_, record) =>
        record.kind === 'sensor' ? <CountedCell record={record} /> : null,
    },
    {
      title: '',
      key: 'refresh',
      width: 56,
      align: 'center',
      render: (_, record) =>
        record.kind === 'sensor' ? <SensorRefresh record={record} /> : null,
    },
  ];

  // Leaf count columns after the name column, in display order, for the totals row.
  const totalColumns = [
    ...ACTIVITY_COLUMNS,
    ...categoryColumns,
    ...MAC_COLUMNS,
  ];
  const totalCells: ReactNode[] = totalColumns.map((column) =>
    formatCount(totals[column.key]),
  );

  return (
    <IBTable<AssetCountRow>
      columns={columns}
      dataSource={rows}
      loading={loading}
      rowKey="key"
      expandable={{
        expandedRowKeys: expandedKeys,
        onExpandedRowsChange: (keys) => onExpandedKeysChange(keys.map(String)),
      }}
      pagination={{
        pageSize,
        showSizeChanger: true,
        onShowSizeChange: (_, size) => setPageSize(size),
      }}
      summary={() =>
        sensors.length > 0 && (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <Typography.Text strong>
                All organizations ({totals.counted} of {sensors.length} sensors
                counted)
              </Typography.Text>
            </Table.Summary.Cell>
            {totalCells.map((cell, idx) => (
              <Table.Summary.Cell key={idx} index={idx + 1} align="right">
                <Typography.Text strong>{cell}</Typography.Text>
              </Table.Summary.Cell>
            ))}
            <Table.Summary.Cell index={totalCells.length + 1} />
            <Table.Summary.Cell index={totalCells.length + 2} />
          </Table.Summary.Row>
        )
      }
    />
  );
};

export default VantageAssetCountsTable;
